import { useRef, useState, useEffect } from "react";
import { Popover, Checkbox, Button } from "@datapay-by-datacom/design-system";
import { PillButton } from "../../../shared/components/DatePill/PillButton";
import {
  SeverityTier,
  SEVERITY_TIER_ORDER,
  SEVERITY_TIER_CONFIG,
} from "../../types/severity-tier";
import type { PayGroupFacet, PayPeriodFacet } from "../../types/shadow-run";
import { formatPayPeriod } from "../../utils/formatters";
import styles from "./PacketFilters.module.css";

export interface PacketFiltersProps {
  severityTiers: SeverityTier[];
  component: string;
  payGroups: string[];
  payPeriodEnds: string[];
  payGroupFacets: PayGroupFacet[];
  payPeriodFacets: PayPeriodFacet[];
  onSeverityChange: (tiers: SeverityTier[]) => void;
  onComponentChange: (component: string) => void;
  onPayGroupsChange: (payGroups: string[]) => void;
  onPayPeriodEndsChange: (payPeriodEnds: string[]) => void;
  onClear: () => void;
  hasActiveFilters: boolean;
}

function formatSeverityValue(tiers: SeverityTier[]): string {
  if (tiers.length === 0) return "All";
  if (tiers.length === 1) return SEVERITY_TIER_CONFIG[tiers[0]].label;
  return `${tiers.length} selected`;
}

function formatComponentValue(component: string): string {
  return component || "All";
}

function formatPayGroupValue(
  codes: string[],
  facets: PayGroupFacet[],
): string {
  if (codes.length === 0) return "All";
  if (codes.length > 1) return `${codes.length} selected`;
  const match = facets.find((f) => f.code === codes[0]);
  return match ? `${match.name} (${match.code})` : codes[0];
}

function formatPayPeriodValue(
  ends: string[],
  facets: PayPeriodFacet[],
): string {
  if (ends.length === 0) return "All";
  if (ends.length > 1) return `${ends.length} selected`;
  const match = facets.find((f) => f.end === ends[0]);
  return match ? formatPayPeriod(match.start, match.end) : ends[0];
}

interface SeverityPillProps {
  severityTiers: SeverityTier[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (tiers: SeverityTier[]) => void;
}

function SeverityPill({
  severityTiers,
  isOpen,
  onOpenChange,
  onChange,
}: SeverityPillProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [pending, setPending] = useState<SeverityTier[]>(severityTiers);

  useEffect(() => {
    if (isOpen) {
      setPending(severityTiers);
    }
  }, [isOpen, severityTiers]);

  function handleToggle(tier: SeverityTier, checked: boolean) {
    setPending((prev) =>
      checked ? [...prev, tier] : prev.filter((t) => t !== tier),
    );
  }

  function handleApply() {
    onChange(pending);
    onOpenChange(false);
  }

  return (
    <>
      <PillButton
        label="Severity"
        value={formatSeverityValue(severityTiers)}
        isActive={severityTiers.length > 0}
        isOpen={isOpen}
        triggerRef={triggerRef}
        onClick={() => onOpenChange(!isOpen)}
        testId="severity-pill"
      />
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom"
      >
        <div className={styles.popoverContent} data-testid="severity-options">
          <div className={styles.checkboxList}>
            {SEVERITY_TIER_ORDER.map((tier) => (
              <Checkbox
                key={tier}
                value={tier}
                isSelected={pending.includes(tier)}
                onChange={(checked: boolean) => handleToggle(tier, checked)}
              >
                <span>{SEVERITY_TIER_CONFIG[tier].label}</span>
              </Checkbox>
            ))}
          </div>
          <div className={styles.popoverActions}>
            <Button variant="primary" onPress={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}

interface PayGroupPillProps {
  payGroups: string[];
  facets: PayGroupFacet[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (groups: string[]) => void;
}

function PayGroupPill({
  payGroups,
  facets,
  isOpen,
  onOpenChange,
  onChange,
}: PayGroupPillProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [pending, setPending] = useState<string[]>(payGroups);

  useEffect(() => {
    if (isOpen) {
      setPending(payGroups);
    }
  }, [isOpen, payGroups]);

  function handleToggle(code: string, checked: boolean) {
    setPending((prev) =>
      checked ? [...prev, code] : prev.filter((group) => group !== code),
    );
  }

  function handleApply() {
    onChange(pending);
    onOpenChange(false);
  }

  return (
    <>
      <PillButton
        label="Pay Group"
        value={formatPayGroupValue(payGroups, facets)}
        isActive={payGroups.length > 0}
        isOpen={isOpen}
        triggerRef={triggerRef}
        onClick={() => onOpenChange(!isOpen)}
        testId="pay-group-pill"
      />
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom"
      >
        <div className={styles.popoverContent} data-testid="pay-group-options">
          <div className={styles.checkboxList}>
            {facets.map((facet) => (
              <Checkbox
                key={facet.code}
                value={facet.code}
                isSelected={pending.includes(facet.code)}
                onChange={(checked: boolean) => handleToggle(facet.code, checked)}
              >
                <span>
                  {facet.name} ({facet.code})
                </span>
              </Checkbox>
            ))}
          </div>
          <div className={styles.popoverActions}>
            <Button variant="primary" onPress={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}

interface PayPeriodPillProps {
  payPeriodEnds: string[];
  facets: PayPeriodFacet[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (periodEnds: string[]) => void;
}

function PayPeriodPill({
  payPeriodEnds,
  facets,
  isOpen,
  onOpenChange,
  onChange,
}: PayPeriodPillProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [pending, setPending] = useState<string[]>(payPeriodEnds);

  useEffect(() => {
    if (isOpen) {
      setPending(payPeriodEnds);
    }
  }, [isOpen, payPeriodEnds]);

  function handleToggle(end: string, checked: boolean) {
    setPending((prev) =>
      checked ? [...prev, end] : prev.filter((periodEnd) => periodEnd !== end),
    );
  }

  function handleApply() {
    onChange(pending);
    onOpenChange(false);
  }

  return (
    <>
      <PillButton
        label="Pay Period"
        value={formatPayPeriodValue(payPeriodEnds, facets)}
        isActive={payPeriodEnds.length > 0}
        isOpen={isOpen}
        triggerRef={triggerRef}
        onClick={() => onOpenChange(!isOpen)}
        testId="pay-period-pill"
      />
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom"
      >
        <div className={styles.popoverContent} data-testid="pay-period-options">
          <div className={styles.checkboxList}>
            {facets.map((facet) => (
              <Checkbox
                key={facet.end}
                value={facet.end}
                isSelected={pending.includes(facet.end)}
                onChange={(checked: boolean) => handleToggle(facet.end, checked)}
              >
                <span>{formatPayPeriod(facet.start, facet.end)}</span>
              </Checkbox>
            ))}
          </div>
          <div className={styles.popoverActions}>
            <Button variant="primary" onPress={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}

interface ComponentPillProps {
  component: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (component: string) => void;
}

function ComponentPill({
  component,
  isOpen,
  onOpenChange,
  onChange,
}: ComponentPillProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [pending, setPending] = useState(component);

  useEffect(() => {
    if (isOpen) {
      setPending(component);
    }
  }, [isOpen, component]);

  function handleApply() {
    onChange(pending.trim());
    onOpenChange(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      handleApply();
    }
  }

  return (
    <>
      <PillButton
        label="Component"
        value={formatComponentValue(component)}
        isActive={component !== ""}
        isOpen={isOpen}
        triggerRef={triggerRef}
        onClick={() => onOpenChange(!isOpen)}
        testId="component-pill"
      />
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom"
      >
        <div className={styles.popoverContent} data-testid="component-input">
          <input
            className={styles.inputField}
            type="text"
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Filter by component…"
            aria-label="Component filter"
            data-testid="component-text-input"
          />
          <div className={styles.popoverActions}>
            <Button variant="primary" onPress={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}

type PopoverName =
  | "severity"
  | "component"
  | "payGroup"
  | "payPeriod"
  | null;

export function PacketFilters({
  severityTiers,
  component,
  payGroups,
  payPeriodEnds,
  payGroupFacets,
  payPeriodFacets,
  onSeverityChange,
  onComponentChange,
  onPayGroupsChange,
  onPayPeriodEndsChange,
  onClear,
  hasActiveFilters,
}: PacketFiltersProps) {
  const [openPopover, setOpenPopover] = useState<PopoverName>(null);

  return (
    <div className={styles.bar}>
      <div className={styles.pills}>
        {payGroupFacets.length > 0 && (
          <PayGroupPill
            payGroups={payGroups}
            facets={payGroupFacets}
            isOpen={openPopover === "payGroup"}
            onOpenChange={(open) =>
              setOpenPopover(open ? "payGroup" : null)
            }
            onChange={onPayGroupsChange}
          />
        )}
        {payPeriodFacets.length > 0 && (
          <PayPeriodPill
            payPeriodEnds={payPeriodEnds}
            facets={payPeriodFacets}
            isOpen={openPopover === "payPeriod"}
            onOpenChange={(open) =>
              setOpenPopover(open ? "payPeriod" : null)
            }
            onChange={onPayPeriodEndsChange}
          />
        )}
        <SeverityPill
          severityTiers={severityTiers}
          isOpen={openPopover === "severity"}
          onOpenChange={(open) =>
            setOpenPopover(open ? "severity" : null)
          }
          onChange={onSeverityChange}
        />
        <ComponentPill
          component={component}
          isOpen={openPopover === "component"}
          onOpenChange={(open) =>
            setOpenPopover(open ? "component" : null)
          }
          onChange={onComponentChange}
        />
      </div>
      {hasActiveFilters && (
        <button
          type="button"
          className={styles.clearAll}
          onClick={onClear}
          data-testid="clear-filters"
        >
          Clear All
        </button>
      )}
    </div>
  );
}
